import React from "react";
import { formatSqlVersion } from "./formatters";

/**
 * Shared DataGrid cell renderers used by both the Dashboard overview grid and the Servers fleet
 * grid. These two grids previously carried near-identical copies of these renderers and had already
 * started to diverge, so they live here alongside formatSqlVersion.
 */

/**
 * Group label: shows the portal-side DisplayName override when one is set, otherwise the group name
 * the server reports. Marked "(renamed)" with the reported name in the tooltip, because a live-site
 * backup restored into a demo environment keeps reporting the original site's group name.
 */
export const renderGroupLabel = (data) => {
  const s = data.data;
  const overridden = !!(s && s.displayName);
  return (
    <span title={overridden ? `Reported group: ${s.groupName}` : undefined}>
      {(s && (s.displayName || s.groupName)) || "-"}
      {overridden && (
        <span style={{ marginLeft: 6, fontSize: "10px", color: "var(--text-muted)" }}>(renamed)</span>
      )}
    </span>
  );
};

export const calculateGroupLabel = (rowData) => rowData.displayName || rowData.groupName;

/** Reduces the full @@VERSION banner to e.g. "SQL Server 2019". */
export const renderSqlVersion = (data) => <span>{formatSqlVersion(data.value)}</span>;
export const calculateSqlVersion = (rowData) => formatSqlVersion(rowData.sqlVersion);

/**
 * Component version cell - green when it matches its target, amber otherwise.
 * `versionField`/`targetField` are the row property names, e.g. ("hoVersion", "targetHoVersion").
 */
export const makeVersionCell = (versionField, targetField) => (data) => {
  const s = data.data;
  const match = s && s[versionField] && s[targetField] && s[versionField] === s[targetField];
  return (
    <span style={{ color: match ? "#1D9E75" : "#EF9F27", fontWeight: "500" }}>
      {data.value || "-"}
    </span>
  );
};

/** Release-channel badge (Demo styled differently from every other channel). */
export const renderTypeBadge = (data) => {
  const type = data.value || "Live";
  const isDemo = type.toLowerCase() === "demo";
  return (
    <span
      className="portal-badge"
      style={{
        backgroundColor: isDemo ? "#2d3748" : "#23395d",
        color: isDemo ? "#a0aec0" : "#93c5fd",
      }}
    >
      {type.toUpperCase()}
    </span>
  );
};

export const renderStatusBadge = (data) => {
  const val = data.value || "";
  const isOnline = val.toLowerCase() === "online";
  return (
    <span
      className="portal-badge"
      style={{
        backgroundColor: isOnline ? "#193c31" : "#442121",
        color: isOnline ? "#52c48d" : "#f57c7c",
      }}
    >
      {val ? val.toUpperCase() : "UNKNOWN"}
    </span>
  );
};

export const renderSsisBadge = (data) => {
  const val = data.value;
  const label = val === true ? "Installed" : val === false ? "Not Installed" : "Unknown";
  const color = val === true ? "#52c48d" : val === false ? "#f57c7c" : "#9aa2b1";
  const bg = val === true ? "#193c31" : val === false ? "#442121" : "#2d3748";
  return (
    <span className="portal-badge" style={{ backgroundColor: bg, color }}>
      {label}
    </span>
  );
};

/**
 * Row highlight shared by both grids: online = green, offline but seen in the last 24h = amber,
 * otherwise red.
 */
export const applyRowStatusClass = (e) => {
  if (e.rowType !== "data") return;
  const server = e.data;
  const isOnline = server?.status?.toLowerCase() === "online";
  const lastHeartbeat = server?.lastHeartbeat ? new Date(server.lastHeartbeat) : null;
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

  if (isOnline) {
    e.rowElement.classList.add("row-success");
  } else if (lastHeartbeat && lastHeartbeat >= oneDayAgo) {
    e.rowElement.classList.add("row-warning");
  } else {
    e.rowElement.classList.add("row-danger");
  }
};
